import type {
  SavedViewGroupByT,
  SavedViewSort,
  SelectedTaskArrangement,
  SortDirectionT,
  SubtaskDisplayT,
  TaskArrangement,
} from '@portfolio-pm/shared';
import { newId } from '@portfolio-pm/shared';
import * as Y from 'yjs';
import {
  type WithNow,
  getDeletedMap,
  getSelectedTaskArrangementsMap,
  getTaskArrangementsMap,
} from './doc.ts';

export interface CreateTaskArrangementInput {
  name: string;
  groupBy?: SavedViewGroupByT;
  groupDirection?: SortDirectionT;
  sort?: SavedViewSort[];
}

export interface UpdateTaskArrangementInput {
  name?: string;
  groupBy?: SavedViewGroupByT;
  groupDirection?: SortDirectionT;
  sort?: SavedViewSort[];
  order?: number;
}

function ts(options: WithNow): number {
  return options.now ?? Date.now();
}

export function listTaskArrangements(doc: Y.Doc): TaskArrangement[] {
  const out: TaskArrangement[] = [];
  for (const [id, inner] of getTaskArrangementsMap(doc).entries()) {
    out.push(taskArrangementFromMap(id, inner));
  }
  return out.sort((a, b) => a.order - b.order || a.createdAt - b.createdAt);
}

export function getTaskArrangement(doc: Y.Doc, id: string): TaskArrangement | undefined {
  const inner = getTaskArrangementsMap(doc).get(id);
  return inner ? taskArrangementFromMap(id, inner) : undefined;
}

export function createTaskArrangement(
  doc: Y.Doc,
  input: CreateTaskArrangementInput,
  options: WithNow = {},
): TaskArrangement {
  const now = ts(options);
  const taskArrangements = getTaskArrangementsMap(doc);
  const id = newId();
  const maxOrder = Math.max(
    -1,
    ...listTaskArrangements(doc).map((arrangement) => arrangement.order),
  );
  const inner = new Y.Map<unknown>();
  inner.set('name', input.name);
  inner.set('groupBy', input.groupBy ?? 'project');
  inner.set('groupDirection', input.groupDirection ?? 'asc');
  inner.set('sort', input.sort ?? [{ key: 'due', direction: 'asc' }]);
  inner.set('order', maxOrder + 1);
  inner.set('createdAt', now);
  inner.set('updatedAt', now);
  taskArrangements.set(id, inner);
  return taskArrangementFromMap(id, inner);
}

export function updateTaskArrangement(
  doc: Y.Doc,
  id: string,
  patch: UpdateTaskArrangementInput,
  options: WithNow = {},
): void {
  const inner = getTaskArrangementsMap(doc).get(id);
  if (!inner) return;
  const now = ts(options);
  doc.transact(() => {
    if (patch.name !== undefined) inner.set('name', patch.name);
    if (patch.groupBy !== undefined) inner.set('groupBy', patch.groupBy);
    if (patch.groupDirection !== undefined) inner.set('groupDirection', patch.groupDirection);
    if (patch.sort !== undefined) inner.set('sort', patch.sort);
    if (patch.order !== undefined) inner.set('order', patch.order);
    inner.set('updatedAt', now);
  });
}

export function deleteTaskArrangement(doc: Y.Doc, id: string, options: WithNow = {}): void {
  const taskArrangements = getTaskArrangementsMap(doc);
  if (!taskArrangements.has(id)) return;
  const now = ts(options);
  doc.transact(() => {
    taskArrangements.delete(id);
    getDeletedMap(doc).set(id, now);
    for (const [listKey, inner] of getSelectedTaskArrangementsMap(doc).entries()) {
      if (inner.get('arrangementId') === id) {
        getSelectedTaskArrangementsMap(doc).delete(listKey);
      }
    }
  });
}

export function listSelectedTaskArrangements(doc: Y.Doc): SelectedTaskArrangement[] {
  const out: SelectedTaskArrangement[] = [];
  for (const [listKey, inner] of getSelectedTaskArrangementsMap(doc).entries()) {
    out.push(selectedTaskArrangementFromMap(listKey, inner));
  }
  return out.sort((a, b) => a.listKey.localeCompare(b.listKey));
}

export function getSelectedTaskArrangement(
  doc: Y.Doc,
  listKey: string,
): SelectedTaskArrangement | undefined {
  const inner = getSelectedTaskArrangementsMap(doc).get(listKey);
  return inner ? selectedTaskArrangementFromMap(listKey, inner) : undefined;
}

export function selectTaskArrangement(
  doc: Y.Doc,
  listKey: string,
  arrangementId: string,
  options: WithNow = {},
): SelectedTaskArrangement {
  const now = ts(options);
  const selected = getSelectedTaskArrangementsMap(doc);
  const existing = selected.get(listKey);
  const inner = existing ?? new Y.Map<unknown>();
  if (existing) {
    if (!inner.has('createdAt')) inner.set('createdAt', now);
  } else {
    inner.set('createdAt', now);
  }
  inner.set('arrangementId', arrangementId);
  inner.set('updatedAt', now);
  if (!existing) selected.set(listKey, inner);
  return selectedTaskArrangementFromMap(listKey, inner);
}

export function setSelectedTaskSubtaskDisplay(
  doc: Y.Doc,
  listKey: string,
  subtaskDisplay: SubtaskDisplayT,
  options: WithNow = {},
): SelectedTaskArrangement {
  const now = ts(options);
  const selected = getSelectedTaskArrangementsMap(doc);
  const existing = selected.get(listKey);
  const inner = existing ?? new Y.Map<unknown>();
  if (!existing) {
    inner.set('arrangementId', 'builtin:default');
    inner.set('createdAt', now);
  }
  inner.set('subtaskDisplay', subtaskDisplay);
  inner.set('updatedAt', now);
  if (!existing) selected.set(listKey, inner);
  return selectedTaskArrangementFromMap(listKey, inner);
}

function taskArrangementFromMap(id: string, inner: Y.Map<unknown>): TaskArrangement {
  const groupBy = inner.get('groupBy');
  const groupDirection = inner.get('groupDirection');
  return {
    id,
    name: String(inner.get('name') ?? ''),
    groupBy:
      typeof groupBy === 'string' &&
      [
        'none',
        'project',
        'tag',
        'priority',
        'due',
        'scheduled',
        'workflow',
        'estimate',
        'owner',
      ].includes(groupBy)
        ? (groupBy as SavedViewGroupByT)
        : 'project',
    groupDirection: groupDirection === 'desc' ? 'desc' : 'asc',
    sort: readSort(inner.get('sort')),
    order: Number(inner.get('order') ?? 0),
    createdAt: Number(inner.get('createdAt') ?? 0),
    updatedAt: Number(inner.get('updatedAt') ?? 0),
  };
}

function selectedTaskArrangementFromMap(
  listKey: string,
  inner: Y.Map<unknown>,
): SelectedTaskArrangement {
  return {
    listKey,
    arrangementId: String(inner.get('arrangementId') ?? ''),
    subtaskDisplay: inner.get('subtaskDisplay') === 'nested' ? 'nested' : 'flat',
    createdAt: Number(inner.get('createdAt') ?? 0),
    updatedAt: Number(inner.get('updatedAt') ?? 0),
  };
}

function readSort(rawSort: unknown): SavedViewSort[] {
  return Array.isArray(rawSort)
    ? (rawSort as SavedViewSort[]).filter(
        (item) =>
          item &&
          typeof item === 'object' &&
          typeof item.key === 'string' &&
          (item.direction === 'asc' || item.direction === 'desc'),
      )
    : [{ key: 'due', direction: 'asc' }];
}
